import { useQuery } from '@tanstack/react-query';
import { testsApi, type Test, type TestDefinition, type TestQuestion } from '@/api/tests';
import {
    ClipboardText,
    DocumentText,
    InfoCircle,
    Send2,
    Gps,
    Profile2User,
} from '@/components/icons';
import { AppSheetLayout } from '@/components/system';
import TestApplicationInstructions from '@/components/tests/TestApplicationInstructions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { getCategoryStyle } from '@/lib/instrument-categories';

interface TestDetailSheetProps {
    test: Test | null;
    isOpen: boolean;
    onClose: () => void;
    onAssign?: (test: Test) => void;
    onPreview?: (test: Test) => void;
}

export const TestDetailSheet = ({
    test,
    isOpen,
    onClose,
    onAssign,
    onPreview,
}: TestDetailSheetProps) => {
    const {
        data: testDefinition,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ['tests', test?.id],
        queryFn: () => testsApi.getById(test?.id || ''),
        enabled: isOpen && Boolean(test?.id),
        retry: false,
    });

    if (!test) return null;

    const resolvedTest: TestDefinition | null = testDefinition ?? null;
    const details = resolvedTest?.details ?? null;

    const sections = resolvedTest?.sections ?? [];
    const questionCount = sections.reduce((total, section) => total + section.questions.length, 0);
    const questionList: TestQuestion[] = sections.flatMap((section) => section.questions);
    const previewQuestions = questionList
        .map((question) => question.field_label || question.field_name)
        .filter(Boolean)
        .slice(0, 6);
    const scoring = details?.scoring_interpretation;
    const factors = scoring?.factors ? Object.entries(scoring.factors) : [];
    const interpretationTable = scoring?.interpretation_table ?? [];
    const hasDetails = Boolean(
        details?.application_instructions ||
        details?.context ||
        details?.indications ||
        details?.target_audience ||
        details?.technical_description ||
        details?.scoring_interpretation,
    );

    const category = test.category ?? details?.category;
    const totalQuestions = questionCount || test.question_count || 0;
    const estimatedMinutes = test.estimated_minutes ?? details?.estimated_minutes;
    const targetAudience = test.target_audience ?? details?.target_audience;
    const hasFooterActions = Boolean(onAssign || onPreview);

    return (
        <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <SheetContent className="flex size-full flex-col overflow-hidden bg-surface-subtle p-0 sm:max-w-xl">
                <AppSheetLayout
                    title={test.name}
                    description={
                        test.description || 'Sem descrição disponível para este instrumento.'
                    }
                    meta={
                        <div className="mt-4 flex flex-wrap gap-2">
                            {category && (
                                <Badge
                                    variant="outline"
                                    className={getCategoryStyle(category).badgeClassName}
                                >
                                    {category}
                                </Badge>
                            )}
                            {test.test_id && <Badge variant="brand">{test.test_id}</Badge>}
                            {totalQuestions > 0 && (
                                <Badge variant="neutral">{totalQuestions} itens</Badge>
                            )}
                            {estimatedMinutes ? (
                                <Badge variant="neutral">~{estimatedMinutes} min</Badge>
                            ) : null}
                            {targetAudience && <Badge variant="neutral">{targetAudience}</Badge>}
                        </div>
                    }
                    footer={
                        hasFooterActions ? (
                            <div className="flex flex-col gap-2 sm:flex-row">
                                {onAssign && (
                                    <Button
                                        variant="tint"
                                        className="w-full justify-center sm:flex-1"
                                        onClick={() => onAssign(test)}
                                    >
                                        <Send2 className="size-4" />
                                        Enviar
                                    </Button>
                                )}
                                {onPreview && (
                                    <Button
                                        variant="surface"
                                        className="w-full justify-center sm:w-auto"
                                        onClick={() => onPreview(test)}
                                    >
                                        <DocumentText className="size-4" />
                                        Visualizar
                                    </Button>
                                )}
                            </div>
                        ) : undefined
                    }
                    bodyClassName="min-h-0"
                >
                    <ScrollArea className="h-full">
                        <div className="space-y-8 p-6 pb-8">
                            {isLoading ? (
                                <div className="py-8">
                                    <Loading className="h-auto" size={120} />
                                </div>
                            ) : isError ? (
                                <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                                    Não foi possível carregar os detalhes deste instrumento.
                                </div>
                            ) : (
                                <>
                                    <TestApplicationInstructions
                                        instructions={details?.application_instructions}
                                        className="border-border bg-card shadow-sm"
                                    />

                                    {details?.attribution && (
                                        <p className="text-xs text-muted-foreground">
                                            {details.attribution}
                                        </p>
                                    )}

                                    {details?.technical_description && (
                                        <section className="space-y-3">
                                            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                                <InfoCircle className="size-4" /> Descrição técnica
                                            </h3>
                                            <p className="rounded-xl border border-border bg-card p-4 leading-relaxed text-foreground shadow-sm">
                                                {details.technical_description}
                                            </p>
                                        </section>
                                    )}

                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        {details?.target_audience && (
                                            <section className="space-y-3">
                                                <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                                    <Profile2User className="size-4" /> Público-alvo
                                                </h3>
                                                <div className="rounded-xl border border-border bg-card p-4 text-foreground shadow-sm">
                                                    {details.target_audience}
                                                </div>
                                            </section>
                                        )}

                                        {details?.context && (
                                            <section className="space-y-3">
                                                <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                                    <Gps className="size-4" /> Contexto de uso
                                                </h3>
                                                <div className="rounded-xl border border-border bg-card p-4 text-foreground shadow-sm">
                                                    {details.context}
                                                </div>
                                            </section>
                                        )}
                                    </div>

                                    {details?.indications && (
                                        <section className="space-y-3">
                                            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                                <Gps className="size-4 text-emerald-600" />{' '}
                                                Indicações
                                            </h3>
                                            <div className="rounded-xl border border-border bg-card p-4 text-foreground shadow-sm">
                                                {details.indications}
                                            </div>
                                        </section>
                                    )}

                                    {questionCount > 0 && (
                                        <section className="space-y-3">
                                            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                                <ClipboardText className="size-4" /> Itens do
                                                instrumento
                                            </h3>
                                            <div className="space-y-3 rounded-xl border border-border bg-card p-4 text-foreground shadow-sm">
                                                <p className="text-sm text-muted-foreground">
                                                    Total de perguntas:{' '}
                                                    <span className="font-medium text-foreground">
                                                        {questionCount}
                                                    </span>
                                                </p>
                                                {previewQuestions.length > 0 ? (
                                                    <ul className="space-y-2">
                                                        {previewQuestions.map((label, index) => (
                                                            <li
                                                                key={`${label}-${index}`}
                                                                className="flex items-start gap-2"
                                                            >
                                                                <span className="mt-2 block size-1.5 flex-shrink-0 rounded-full bg-info" />
                                                                {label}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    <p className="text-sm text-muted-foreground">
                                                        Nenhuma pergunta listada para visualização.
                                                    </p>
                                                )}
                                            </div>
                                        </section>
                                    )}

                                    {(scoring?.scoring_rules ||
                                        factors.length > 0 ||
                                        interpretationTable.length > 0) && (
                                        <section className="space-y-4">
                                            <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                                                <InfoCircle className="size-4" /> Pontuação e
                                                interpretação
                                            </h3>

                                            {scoring?.scoring_rules && (
                                                <div className="rounded-xl border border-border bg-card p-4 text-foreground shadow-sm">
                                                    <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                                                        Regras de pontuação
                                                    </p>
                                                    <p>{scoring.scoring_rules}</p>
                                                </div>
                                            )}

                                            {factors.length > 0 && (
                                                <div className="rounded-xl border border-border bg-card p-4 text-foreground shadow-sm">
                                                    <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                                                        Fatores
                                                    </p>
                                                    <div className="space-y-2">
                                                        {factors.map(([key, description]) => (
                                                            <div key={key} className="text-sm">
                                                                <span className="font-semibold text-foreground">
                                                                    {key}:
                                                                </span>{' '}
                                                                <span className="text-muted-foreground">
                                                                    {description}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {interpretationTable.length > 0 && (
                                                <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
                                                    <div className="border-b border-border px-4 py-3 text-xs font-semibold uppercase text-muted-foreground">
                                                        Tabela de interpretação
                                                    </div>
                                                    <table className="w-full text-sm text-foreground">
                                                        <thead className="bg-surface">
                                                            <tr>
                                                                <th className="px-4 py-2 text-left font-semibold">
                                                                    Percentil
                                                                </th>
                                                                <th className="px-4 py-2 text-left font-semibold">
                                                                    Interpretação
                                                                </th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {interpretationTable.map(
                                                                (row, index) => (
                                                                    <tr
                                                                        key={`${row.percentile}-${index}`}
                                                                        className="border-t border-border"
                                                                    >
                                                                        <td className="px-4 py-2">
                                                                            {row.percentile}
                                                                        </td>
                                                                        <td className="px-4 py-2">
                                                                            {row.interpretation}
                                                                        </td>
                                                                    </tr>
                                                                ),
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </section>
                                    )}

                                    {!hasDetails && questionCount === 0 && (
                                        <div className="rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
                                            Informações detalhadas ainda não disponíveis para este
                                            instrumento.
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </ScrollArea>
                </AppSheetLayout>
            </SheetContent>
        </Sheet>
    );
};
